import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  fold,
} from '@portal/ui';
import { apiFetch, useLocalize } from '@portal/web-sdk';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { FileSearch, Languages, Loader2, SunMoon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../theme';
import { NavIcon } from './icons';
import { flattenNavigation, useFavorites, useNavigation } from './navigation';
import { useRecentPages } from './recent';

interface SearchResults {
  groups: Array<{
    key: string;
    label: { vi: string; en?: string };
    items: Array<{ id: string; title: string; subtitle: string | null; path: string }>;
  }>;
}

/** Marks server results: the server already matched them, the palette must not filter them again. */
const SERVER_RESULT = '__server__';

/**
 * ⌘K / Ctrl+K. Pages the user can reach, shell actions, and records of every module found by the
 * global search (`GET /system/search`, search providers of the modules). Accents are ignored.
 */
export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const localize = useLocalize();
  const navigate = useNavigate();
  const { resolved, setPreference } = useTheme();
  const nav = useNavigation();
  const pages = flattenNavigation(nav.data?.items ?? []);
  const byId = new Map(pages.map((p) => [p.node.id, p]));
  const favorites = useFavorites().navIds.flatMap((id) => byId.get(id) ?? []);
  const recent = useRecentPages().flatMap((id) => byId.get(id) ?? []);

  const [input, setInput] = useState('');
  const query = useDebounced(input.trim(), 250);
  const results = useQuery({
    queryKey: ['system', 'search', query],
    queryFn: () =>
      apiFetch<SearchResults>(`system/search?${new URLSearchParams({ q: query, limit: '5' })}`),
    enabled: open && query.length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const groups = query.length >= 2 ? (results.data?.groups ?? []) : [];

  const run = (action: () => void) => {
    onOpenChange(false);
    setInput('');
    action();
  };

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('command.title')}
      filter={(value, search, keywords) =>
        keywords?.includes(SERVER_RESULT) || fold(value).includes(fold(search)) ? 1 : 0
      }
    >
      <CommandInput placeholder={t('shell.search')} value={input} onValueChange={setInput} />
      <CommandList>
        <CommandEmpty>
          {results.isFetching ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="size-4 animate-spin" />
              {t('command.searching')}
            </span>
          ) : results.isError ? (
            t('command.searchFailed')
          ) : (
            t('command.empty')
          )}
        </CommandEmpty>
        {groups.map((group) => (
          <CommandGroup key={group.key} heading={localize(group.label)}>
            {group.items.map((item) => (
              <CommandItem
                key={`${group.key}-${item.id}`}
                value={`${group.key} ${item.id} ${item.title}`}
                keywords={[SERVER_RESULT]}
                onSelect={() => run(() => void navigate({ href: item.path }))}
              >
                <FileSearch />
                <span className="min-w-0 truncate">{item.title}</span>
                {item.subtitle ? (
                  <span className="ml-auto max-w-[45%] truncate text-xs text-muted-foreground">
                    {item.subtitle}
                  </span>
                ) : null}
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
        {[
          { key: 'favorites', heading: t('command.favorites'), entries: favorites },
          { key: 'recent', heading: t('command.recent'), entries: recent },
        ].map((group) =>
          group.entries.length > 0 ? (
            <CommandGroup key={group.key} heading={group.heading}>
              {group.entries.map(({ node, trail }) => {
                const label = trail.map((n) => localize(n.label)).join(' › ');
                return (
                  <CommandItem
                    key={`${group.key}-${node.id}`}
                    value={`${group.key} ${label} ${node.label.vi} ${node.label.en ?? ''}`}
                    onSelect={() => run(() => void navigate({ to: node.path! }))}
                  >
                    <NavIcon name={node.icon} />
                    {label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          ) : null,
        )}
        <CommandGroup heading={t('command.pages')}>
          {pages.map(({ node, trail }) => {
            const label = trail.map((n) => localize(n.label)).join(' › ');
            return (
              <CommandItem
                key={node.id}
                value={`${label} ${node.label.vi} ${node.label.en ?? ''}`}
                onSelect={() => run(() => void navigate({ to: node.path! }))}
              >
                <NavIcon name={node.icon} />
                {label}
              </CommandItem>
            );
          })}
        </CommandGroup>
        <CommandGroup heading={t('command.preferences')}>
          <CommandItem
            onSelect={() => run(() => setPreference(resolved === 'dark' ? 'light' : 'dark'))}
          >
            <SunMoon />
            {t('command.toggleTheme')}
          </CommandItem>
          <CommandItem
            onSelect={() =>
              run(() => void i18n.changeLanguage(i18n.language === 'vi' ? 'en' : 'vi'))
            }
          >
            <Languages />
            {t('command.switchLanguage')}
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}

/** The value once it has stopped changing for `delay` ms (one request per pause in typing). */
function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}
